/**
 * Net Revenue math — shared source of truth for the pricing-v2 workspace
 * KPI strip and ledger.
 *
 * Definition: Net to Lendwise is based on the updated revenue rows
 * excluding LO Compensation.
 *
 * Updated revenue = original margin adjustments − waterfall concession
 * absorption − LO comp reduction, all applied bucket-by-bucket. The LO
 * compensation bucket is then excluded from the Net to Lendwise total.
 */

import type { RateLockPriceAdjustment } from '../types';
import type { LendwiseConcession } from '@/services/lendwiseConcessionService';

export type RevenueBucket = 'lo_compensation' | 'level1' | 'branch_management' | 'margin' | 'other';

/** 4-decimal rounding — matches the legacy `round4` helper. */
export function round4(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/** 1 point = 1% of loan. */
export function pointsToDollars(points: number, loanAmount: number): number {
  return (points / 100) * loanAmount;
}

/**
 * Classify a margin-adjustment description into its revenue bucket.
 */
export function getRevenueBucket(description: string | null | undefined): RevenueBucket {
  if (!description) {
    return 'other';
  }
  const normalized = description.trim().toLowerCase();
  if (normalized === 'lo compensation') {
    return 'lo_compensation';
  }
  if (normalized === 'level 1') {
    return 'level1';
  }
  if (normalized === 'branch management') {
    return 'branch_management';
  }
  if (normalized === 'uncategorized') {
    return 'margin';
  }
  return 'other';
}

export interface RevenueRow {
  description: string;
  bucket: RevenueBucket;
  originalPoints: number;
  updatedPoints: number;
}

export interface NetRevenueResult {
  rows: RevenueRow[];
  /** Sum of updated points across every row (all buckets). */
  updatedTotalMarginPoints: number;
  /** Net to Lendwise — updated points, excluding the `lo_compensation` bucket. */
  netToLendwisePoints: number;
  /** Net to Lendwise in dollars. */
  netToLendwiseDollars: number;
  /** Updated points that flow to the LO compensation bucket (after reduction). */
  loCompensationUpdatedPoints: number;
  /** Updated LO compensation in dollars. */
  loCompensationDollars: number;
}

export interface NetRevenueInput {
  /** Profit-margin rate-lock adjustments (already filtered to priceAdjustmentType='ProfitMargin'). */
  marginAdjustments: RateLockPriceAdjustment[];
  /** Active (non-legacy) concessions with their waterfall absorption fields. */
  activeConcessions: LendwiseConcession[];
  /**
   * Effective LO comp reduction in bps (clamped at Encompass base).
   * Converted to points here.
   */
  appliedReductionBps: number;
  loanAmount: number;
}

/**
 * Compute the updated revenue rows and the Net to Lendwise total.
 *
 * Absorption order: for each margin adjustment in its original order,
 * subtract the applicable bucket's remaining pool (level1 /
 * branch_management / margin / lo_compensation) until exhausted.
 * Remaining pools don't cross buckets.
 */
export function computeNetRevenue(input: NetRevenueInput): NetRevenueResult {
  const { marginAdjustments, activeConcessions, appliedReductionBps, loanAmount } = input;

  const totals = activeConcessions.reduce(
    (acc, c) => ({
      level1: round4(acc.level1 + (Number(c.level1_absorbed) || 0)),
      branchManagement: round4(acc.branchManagement + (Number(c.branch_management_absorbed) || 0)),
      margin: round4(acc.margin + (Number(c.margin_absorbed) || 0))
    }),
    { level1: 0, branchManagement: 0, margin: 0 }
  );

  let remainingLevel1 = totals.level1;
  let remainingBranchManagement = totals.branchManagement;
  let remainingMargin = totals.margin;
  let remainingLoReduction = appliedReductionBps / 100;

  const rows: RevenueRow[] = marginAdjustments.map((adj) => {
    const originalPoints = Math.abs(Number(adj.rate) || 0);
    const bucket = getRevenueBucket(adj.description);

    let appliedReduction = 0;
    if (bucket === 'lo_compensation' && remainingLoReduction > 0) {
      appliedReduction = Math.min(originalPoints, remainingLoReduction);
      remainingLoReduction = round4(remainingLoReduction - appliedReduction);
    } else if (bucket === 'level1' && remainingLevel1 > 0) {
      appliedReduction = Math.min(originalPoints, remainingLevel1);
      remainingLevel1 = round4(remainingLevel1 - appliedReduction);
    } else if (bucket === 'branch_management' && remainingBranchManagement > 0) {
      appliedReduction = Math.min(originalPoints, remainingBranchManagement);
      remainingBranchManagement = round4(remainingBranchManagement - appliedReduction);
    } else if (bucket === 'margin' && remainingMargin > 0) {
      appliedReduction = Math.min(originalPoints, remainingMargin);
      remainingMargin = round4(remainingMargin - appliedReduction);
    }

    const updatedPoints = round4(Math.max(0, originalPoints - appliedReduction));
    return {
      description: adj.description,
      bucket,
      originalPoints,
      updatedPoints
    };
  });

  const updatedTotalMarginPoints = round4(rows.reduce((s, r) => s + r.updatedPoints, 0));
  const netToLendwisePoints = round4(
    rows.filter((r) => r.bucket !== 'lo_compensation').reduce((s, r) => s + r.updatedPoints, 0)
  );
  const loCompensationUpdatedPoints = round4(
    rows.filter((r) => r.bucket === 'lo_compensation').reduce((s, r) => s + r.updatedPoints, 0)
  );

  return {
    rows,
    updatedTotalMarginPoints,
    netToLendwisePoints,
    netToLendwiseDollars: pointsToDollars(netToLendwisePoints, loanAmount),
    loCompensationUpdatedPoints,
    loCompensationDollars: pointsToDollars(loCompensationUpdatedPoints, loanAmount)
  };
}
